import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { FileUploader } from "@/components/ui/file-uploader";
import { AttachmentList } from "@/components/ui/attachment-list";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import {
  CheckCircle, AlertTriangle, User, Home, MapPin, Car, ShieldCheck, Lock,
  FileText, Upload, Building,
} from "lucide-react";
import type {
  CustomerVerification, PropertyType, AccessDifficulty, ParkingSituation, OwnershipType,
} from "@/data/quoteTypes";
import { isVerificationComplete, getVerificationErrors } from "@/data/quoteTypes";

const PROPERTY_TYPES: PropertyType[] = ["Single Family Home", "Condo / Apartment", "Townhome", "Mobile Home", "Other"];
const ACCESS_OPTIONS: AccessDifficulty[] = ["Low", "Medium", "High"];
const PARKING_OPTIONS: ParkingSituation[] = ["Easy", "Limited", "Difficult"];
const DEFAULT_PROPERTY_DETAILS: CustomerVerification["propertyDetails"] = {
  propertyType: "",
  accessDifficulty: "",
  parkingSituation: "",
};

interface Props {
  verification: CustomerVerification;
  address: string;
  onVerificationChange: (v: CustomerVerification) => void;
  onAddressChange: (a: string) => void;
  locked: boolean;
  /** The quote ID for attaching documents */
  quoteId?: string;
  /** Pre-fill data from existing customer record */
  prefillData?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    address?: string;
  };
}

export function CustomerPropertyVerification({ verification, address, onVerificationChange, onAddressChange, locked, quoteId, prefillData }: Props) {
  const v: CustomerVerification = {
    ...verification,
    propertyDetails: {
      ...DEFAULT_PROPERTY_DETAILS,
      ...verification.propertyDetails,
    },
  };
  const complete = isVerificationComplete(v, address);
  const [prefilled, setPrefilled] = useState(false);
  const errors = useMemo(() => getVerificationErrors(v, address), [v, address]);
  const [poaUploading, setPoaUploading] = useState(false);


  // Handle POA document upload
  const handlePoaUpload = async (attachments: Attachment[]) => {
    if (attachments.length > 0) {
      // Mark as uploaded - the attachment is stored in the attachments table
      update({ poaDocumentUploaded: true });
    }
    setPoaUploading(false);
  };

  // Auto-prefill from existing data on mount (only once)
  useEffect(() => {
    if (prefilled || locked) return;
    if (!prefillData) return;
    const needsPrefill = !v.primaryFirstName && !v.primaryLastName && prefillData.firstName;
    if (!needsPrefill) return;
    const next = { ...v };
    if (prefillData.firstName) next.primaryFirstName = prefillData.firstName;
    if (prefillData.lastName) next.primaryLastName = prefillData.lastName;
    if (prefillData.phone) next.primaryPhone = prefillData.phone;
    if (prefillData.email) next.primaryEmail = prefillData.email;
    next.verificationComplete = isVerificationComplete(next, prefillData.address || address);
    onVerificationChange(next);
    if (prefillData.address && !address) onAddressChange(prefillData.address);
    setPrefilled(true);
  }, [prefillData, prefilled, locked]);

  const update = (patch: Partial<CustomerVerification>) => {
    const next = { ...v, ...patch };
    next.verificationComplete = isVerificationComplete(next, address);
    onVerificationChange(next);
  };

  const updateProperty = (patch: Partial<CustomerVerification["propertyDetails"]>) => {
    const next = { ...v, propertyDetails: { ...v.propertyDetails, ...patch } };
    next.verificationComplete = isVerificationComplete(next, address);
    onVerificationChange(next);
  };

  const handleAddressChange = (a: string) => {
    onAddressChange(a);
    const next = { ...v };
    next.verificationComplete = isVerificationComplete(next, a);
    onVerificationChange(next);
  };

  return (
    <Card className={`shadow-none ${complete ? "border-success/30" : "border-warning/30"}`}>
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between pb-1.5 border-b">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Customer & Property Verification</h2>
          </div>
          {complete ? (
            <Badge className="bg-success/10 text-success border-success/30 text-[9px] gap-1">
              <CheckCircle className="h-2.5 w-2.5" /> Complete
            </Badge>
          ) : (
            <Badge variant="outline" className="text-warning border-warning/30 bg-warning/10 text-[9px] gap-1">
              <AlertTriangle className="h-2.5 w-2.5" /> Incomplete
            </Badge>
          )}
        </div>

        {locked && (
          <div className="flex items-center gap-2 p-2 rounded bg-muted/30 border text-[10px] text-muted-foreground">
            <Lock className="h-3 w-3 shrink-0" /> Locked after contract execution
          </div>
        )}

        {/* Primary Customer */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Primary Customer <span className="text-destructive">*</span></p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px]">First Name *</Label>
              <Input className="mt-0.5 h-8 text-xs" value={v.primaryFirstName} disabled={locked}
                onChange={(e) => update({ primaryFirstName: e.target.value })} placeholder="First name" />
                {errors.primaryFirstName && <p className="text-[10px] text-destructive mt-0.5">{errors.primaryFirstName}</p>}
            </div>
            <div>
              <Label className="text-[10px]">Last Name *</Label>
              <Input className="mt-0.5 h-8 text-xs" value={v.primaryLastName} disabled={locked}
                onChange={(e) => update({ primaryLastName: e.target.value })} placeholder="Last name" />
                {errors.primaryLastName && <p className="text-[10px] text-destructive mt-0.5">{errors.primaryLastName}</p>}
            </div>
            <div>
              <Label className="text-[10px]">Phone *</Label>
              <Input className="mt-0.5 h-8 text-xs" value={v.primaryPhone} disabled={locked}
                onChange={(e) => update({ primaryPhone: formatPhoneNumber(e.target.value) })} placeholder="512-555-1234" />
                {errors.primaryPhone && <p className="text-[10px] text-destructive mt-0.5">{errors.primaryPhone}</p>}
            </div>
            <div>
              <Label className="text-[10px]">Email *</Label>
              <Input className="mt-0.5 h-8 text-xs" value={v.primaryEmail} disabled={locked} type="email"
                onChange={(e) => update({ primaryEmail: e.target.value })} placeholder="email@example.com" />
                {errors.primaryEmail && <p className="text-[10px] text-destructive mt-0.5">{errors.primaryEmail}</p>}
            </div>
          </div>
        </div>

        {/* Secondary Customer */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Secondary Customer</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground">Add secondary</span>
              <Switch checked={v.hasSecondaryContact} disabled={locked}
                onCheckedChange={(checked) => update({ hasSecondaryContact: checked })} />
            </div>
          </div>
          {v.hasSecondaryContact && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">First Name</Label>
                <Input className="mt-0.5 h-8 text-xs" value={v.secondaryFirstName} disabled={locked}
                  onChange={(e) => update({ secondaryFirstName: e.target.value })} placeholder="First name" />
              </div>
              <div>
                <Label className="text-[10px]">Last Name</Label>
                <Input className="mt-0.5 h-8 text-xs" value={v.secondaryLastName} disabled={locked}
                  onChange={(e) => update({ secondaryLastName: e.target.value })} placeholder="Last name" />
              </div>
              <div>
                <Label className="text-[10px]">Phone</Label>
                <Input className="mt-0.5 h-8 text-xs" value={v.secondaryPhone} disabled={locked}
                  onChange={(e) => update({ secondaryPhone: formatPhoneNumber(e.target.value) })} placeholder="Optional" />
              </div>
              <div>
                <Label className="text-[10px]">Email</Label>
                <Input className="mt-0.5 h-8 text-xs" value={v.secondaryEmail} disabled={locked} type="email"
                  onChange={(e) => update({ secondaryEmail: e.target.value })} placeholder="Optional" />
              </div>
            </div>
          )}
        </div>

        {/* Address */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Project Address <span className="text-destructive">*</span></p>
          </div>
          <AddressAutocomplete value={address} onChange={handleAddressChange} disabled={locked} className="h-8 text-xs" />
          {errors.address && <p className="text-[10px] text-destructive mt-0.5">{errors.address}</p>}
          {address.trim() && !v.addressVerified && (
            <Button size="sm" variant="outline" className="mt-1.5 h-7 text-[10px] gap-1"
              onClick={() => update({ addressVerified: true })}>
              <CheckCircle className="h-3 w-3" /> Confirm Address
            </Button>
          )}
          {v.addressVerified && (
            <div className="flex items-center gap-1 mt-1 text-[10px] text-success">
              <CheckCircle className="h-3 w-3" /> Address verified
            </div>
          )}
        </div>

        {/* Ownership / POA */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <FileText className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Ownership <span className="text-destructive">*</span></p>
          </div>
          <Select value={v.ownershipType || ""} disabled={locked}
            onValueChange={(val) => update({ ownershipType: val as OwnershipType, poaDocumentUploaded: val === "Homeowner" ? false : v.poaDocumentUploaded })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select ownership type…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Homeowner">Homeowner</SelectItem>
              <SelectItem value="POA">Power of Attorney (POA)</SelectItem>
            </SelectContent>
          </Select>
          {errors.ownershipType && <p className="text-[10px] text-destructive mt-0.5">{errors.ownershipType}</p>}
          {v.ownershipType === "POA" && (
            <div className="mt-2 p-2 rounded border bg-muted/20 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium">POA Document</span>
                {v.poaDocumentUploaded ? (
                  <Badge className="bg-success/10 text-success border-success/30 text-[9px] gap-1">
                    <CheckCircle className="h-2.5 w-2.5" /> Uploaded
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-warning border-warning/30 bg-warning/10 text-[9px] gap-1">
                    <AlertTriangle className="h-2.5 w-2.5" /> Required
                  </Badge>
                )}
              </div>
              {quoteId && !v.poaDocumentUploaded ? (
                <FileUploader
                  parentType="quote"
                  parentId={quoteId}
                  attachmentType="document"
                  acceptedTypes={['application/pdf']}
                  maxFiles={1}
                  maxSizeMB={10}
                  onUploadComplete={handlePoaUpload}
                  onUploadError={(err) => console.error('POA upload failed:', err)}
                  disabled={locked || poaUploading}
                  className="mt-2"
                />
              ) : (
                <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 w-full" disabled={locked || !quoteId}
                  onClick={() => v.poaDocumentUploaded ? null : setPoaUploading(true)}>
                  <Upload className="h-3 w-3" /> {v.poaDocumentUploaded ? "Replace POA Document" : "Upload POA Document"}
                </Button>
              )}
              {!v.poaDocumentUploaded && (
                <p className="text-[9px] text-destructive">POA document required before material ordering can proceed.</p>
              )}
            </div>
          )}
        </div>

        {/* HOA Involvement */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Building className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">HOA Involvement <span className="text-destructive">*</span></p>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant={v.hoaInvolved === true ? "default" : "outline"}
              className="h-7 text-[10px]" disabled={locked}
              onClick={() => update({ hoaInvolved: true })}>
              Yes
            </Button>
            <Button size="sm" variant={v.hoaInvolved === false ? "default" : "outline"}
              className="h-7 text-[10px]" disabled={locked}
              onClick={() => update({ hoaInvolved: false })}>
              No
            </Button>
          </div>
          {v.hoaInvolved === true && (
            <div className="mt-1.5 flex items-center gap-1 text-[10px] text-warning">
              <AlertTriangle className="h-3 w-3" /> HOA involvement flagged — will appear on quote, contract, and job.
              {errors.hoaInvolved && <p className="text-[10px] text-destructive mt-0.5">{errors.hoaInvolved}</p>}
            </div>
          )}
        </div>

        {/* Property Type */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Home className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Property & Access <span className="text-destructive">*</span></p>
          </div>
          <div className="space-y-2">
            <div>
              <Label className="text-[10px]">Property Type *</Label>
              <Select value={v.propertyDetails.propertyType} disabled={locked}
                onValueChange={(val) => {
                  const patch: Partial<CustomerVerification["propertyDetails"]> = { propertyType: val as PropertyType };
                  if (val === "Condo / Apartment") patch.condoDetails = { floorNumber: "", elevatorAvailable: false, serviceElevator: false, hoaApprovalRequired: false };
                  if (val === "Mobile Home") patch.mobileHomeDetails = { plumbingConstraintFlag: false, accessStructuralNotes: "" };
                  if (val !== "Condo / Apartment") patch.condoDetails = undefined;
                  if (val !== "Mobile Home") patch.mobileHomeDetails = undefined;
                  updateProperty(patch);
                }}>
                <SelectTrigger className="mt-0.5 h-8 text-xs"><SelectValue placeholder="Select type…" /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.propertyType && <p className="text-[10px] text-destructive mt-0.5">{errors.propertyType}</p>}
            </div>

            {/* Condo fields */}
            {v.propertyDetails.propertyType === "Condo / Apartment" && v.propertyDetails.condoDetails && (
              <div className="grid grid-cols-2 gap-2 p-2 rounded border bg-muted/20">
                <div>
                  <Label className="text-[10px]">Floor Number *</Label>
                  <Input className="mt-0.5 h-8 text-xs" disabled={locked}
                    value={v.propertyDetails.condoDetails.floorNumber}
                    onChange={(e) => updateProperty({ condoDetails: { ...v.propertyDetails.condoDetails!, floorNumber: e.target.value } })} />
                    {errors.floorNumber && <p className="text-[10px] text-destructive mt-0.5">{errors.floorNumber}</p>}
                </div>
                <div className="flex items-center gap-2 pt-4">
                  <Switch checked={v.propertyDetails.condoDetails.elevatorAvailable} disabled={locked}
                    onCheckedChange={(c) => updateProperty({ condoDetails: { ...v.propertyDetails.condoDetails!, elevatorAvailable: c } })} />
                  <span className="text-[10px]">Elevator</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={v.propertyDetails.condoDetails.serviceElevator} disabled={locked}
                    onCheckedChange={(c) => updateProperty({ condoDetails: { ...v.propertyDetails.condoDetails!, serviceElevator: c } })} />
                  <span className="text-[10px]">Service Elevator</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={v.propertyDetails.condoDetails.hoaApprovalRequired} disabled={locked}
                    onCheckedChange={(c) => updateProperty({ condoDetails: { ...v.propertyDetails.condoDetails!, hoaApprovalRequired: c } })} />
                  <span className="text-[10px]">HOA Approval Req.</span>
                </div>
              </div>
            )}

            {/* Mobile home fields */}
            {v.propertyDetails.propertyType === "Mobile Home" && v.propertyDetails.mobileHomeDetails && (
              <div className="space-y-2 p-2 rounded border bg-muted/20">
                <div className="flex items-center gap-2">
                  <Switch checked={v.propertyDetails.mobileHomeDetails.plumbingConstraintFlag} disabled={locked}
                    onCheckedChange={(c) => updateProperty({ mobileHomeDetails: { ...v.propertyDetails.mobileHomeDetails!, plumbingConstraintFlag: c } })} />
                  <span className="text-[10px]">Plumbing Constraint Flag</span>
                </div>
                <div>
                  <Label className="text-[10px]">Access / Structural Notes</Label>
                  <Textarea className="mt-0.5 text-xs" rows={2} disabled={locked}
                    value={v.propertyDetails.mobileHomeDetails.accessStructuralNotes}
                    onChange={(e) => updateProperty({ mobileHomeDetails: { ...v.propertyDetails.mobileHomeDetails!, accessStructuralNotes: e.target.value } })} />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Access Difficulty *</Label>
                <Select value={v.propertyDetails.accessDifficulty} disabled={locked}
                  onValueChange={(val) => updateProperty({ accessDifficulty: val as AccessDifficulty })}>
                  <SelectTrigger className="mt-0.5 h-8 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {ACCESS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.accessDifficulty && <p className="text-[10px] text-destructive mt-0.5">{errors.accessDifficulty}</p>}
              </div>
              <div>
                <Label className="text-[10px]">Parking Situation *</Label>
                <div className="flex items-center gap-1 mt-0.5">
                  <Car className="h-3.5 w-3.5 text-muted-foreground" />
                  <Select value={v.propertyDetails.parkingSituation} disabled={locked}
                    onValueChange={(val) => updateProperty({ parkingSituation: val as ParkingSituation })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {PARKING_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {errors.parkingSituation && <p className="text-[10px] text-destructive mt-0.5">{errors.parkingSituation}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Completion message */}
        {!complete && (
          <div className="flex items-center gap-2 p-2 rounded bg-warning/5 border border-warning/20 text-[10px] text-warning">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            Complete customer and property details before building the quote.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
